import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = ({ appName, appIcon, appColor, storageKey }) => {
  // Disabled per user request - no install prompts
  return null;

  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    if (checkInstalled()) {
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      // Don't auto-show, wait for timer
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('App installed');
      setIsInstalled(true);
      setShow(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Show prompt after delay
    const timer = setTimeout(() => {
      if (!checkInstalled()) {
        setShow(true);
        console.log('Showing install prompt');
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(timer);
    };
  }, []);

  const handleInstallClick = async () => {
    console.log('Install button clicked', { hasDeferredPrompt: !!deferredPrompt });
    
    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt();
        
        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;
        console.log('User response:', outcome);
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setIsInstalled(true);
          setShow(false);
        } else {
          console.log('User dismissed the install prompt');
        }
        
        // Clear the deferredPrompt
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error during installation:', error);
      }
    } else {
      // Manual installation instructions
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        // iOS
        alert('📱 To install on iOS:\n\n1. Tap the Share button (box with arrow)\n2. Scroll and tap "Add to Home Screen"\n3. Tap "Add"');
      } else if (navigator.userAgent.includes('Android')) {
        // Android - check browser menu
        alert('📱 To install:\n\n1. Tap the menu (⋮) in your browser\n2. Look for "Install app" or "Add to Home screen"\n3. Tap to install');
      } else {
        // Desktop
        alert('💻 To install:\n\n1. Look for the install icon (⊕) in the address bar\n2. Or check browser menu for "Install" option');
      }
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  if (!show || isInstalled) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <Card className="bg-slate-900/95 backdrop-blur-md border border-slate-700 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${appColor}, ${appColor}dd)` }}
          >
            <span className="text-2xl">{appIcon}</span>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold mb-1">{appName}</h3>
            <p className="text-sm text-slate-400 mb-3">
              Install app for quick access and offline use
            </p>
            
            <div className="flex gap-2">
              <Button
                onClick={handleInstallClick}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Install
              </Button>
              <Button
                onClick={handleClose}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Install Hint for iOS */}
        {/iPad|iPhone|iPod/.test(navigator.userAgent) && (
          <div className="mt-3 pt-3 border-t border-slate-700">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Smartphone className="w-3 h-3" />
              <span>Tap Share → Add to Home Screen</span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;
